// ── Clock-In countdown-completion alarm (Android) ───────────────────────────
// Reuses the same native AlarmScheduler plugin (android/app/.../
// AlarmSchedulerPlugin.java) that task/habit reminder alarms ride on —
// AlarmManager.setAlarmClock() + AlarmReceiver → AlarmRingtoneService →
// AlarmActivity — so a Clock-In countdown rings reliably even if the app is
// backgrounded or fully closed when it hits zero: a full-screen "time's up"
// takeover if the screen is locked/off, a heads-up notification with sound
// if it's already unlocked/in use, looping the sound until dismissed either
// way. Unlike reminder alarms (a batch diffed against Firestore data by
// alarmSync.js), this is a single alarm scheduled/cancelled directly by
// FocusTab.jsx on every relevant start/pause/resume/stop — see
// scheduleTimerAlarm/cancelTimerAlarm below. No-ops entirely outside a
// native build.
import { registerPlugin, Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { CLOCK_IN_ALARM_SOUND_ID } from '../constants/misc';

const AlarmScheduler = registerPlugin('AlarmScheduler');
const isNative = Capacitor.isNativePlatform();

// Same "ask the first time it's actually needed" pattern as alarmSync.js.
let permissionRequested = false;
async function ensureNotificationPermission() {
  if (permissionRequested) return;
  try {
    const status = await PushNotifications.checkPermissions();
    if (status.receive !== 'granted') await PushNotifications.requestPermissions();
    permissionRequested = true;
  } catch { /* best-effort */ }
}

// triggerAt: epoch ms the countdown hits zero. title/body show on the
// ringing notification and lock-screen takeover (title only there).
export async function scheduleTimerAlarm({ triggerAt, title, body }) {
  if (!isNative) return;
  // Hand the alarm to AlarmManager BEFORE touching notification permission.
  // setAlarmClock() doesn't need POST_NOTIFICATIONS — that's only needed for
  // the notification AlarmRingtoneService posts when the alarm actually
  // fires, which is minutes away. Awaiting it here used to put a full
  // Capacitor bridge round-trip in front of the scheduling call on the first
  // countdown of every fresh app launch (permissionRequested starts false),
  // while cancelTimerAlarm() below has no such gate. FocusTab's notification
  // effect fires that ungated cancel on every re-run while the app is still
  // hydrating, so a cancel dispatched just before the schedule could land
  // just after it — setting the alarm and then immediately clearing it. The
  // countdown then ran to zero in silence and only rang when the phone was
  // next woken. Second and later countdowns worked because the flag was
  // already true by then, which is what made this look intermittent.
  try {
    await AlarmScheduler.scheduleTimerAlarm({
      triggerAt,
      title: title || 'Clock-In',
      body: body || 'Session complete',
      alarmSound: CLOCK_IN_ALARM_SOUND_ID,
    });
  } catch { /* best-effort — never block the in-app timer on this */ }
  ensureNotificationPermission();
}

export async function cancelTimerAlarm() {
  if (!isNative) return;
  try { await AlarmScheduler.cancelTimerAlarm(); } catch { /* best-effort */ }
}

export { isNative as timerAlarmIsNative };
